import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import debounce from 'lodash/debounce';
import PayoutDateComponent from "./component";
import Sidebar from "../../../component/Navigation/Sidebar/Sidebar";
import Papa from 'papaparse';

const PayoutDateContainer = (props) => {
  const navigate = useNavigate();
  const [payoutDate, setPayoutDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [convertedFile, setConvertedFile] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [requiredColumns, setRequiredColumns] = useState([]);

  const isFormValid = payoutDate && selectedFile;

  // Create ref for file input
  const fileInputRef = useRef(null);

  // Convert user selected date to API format (YYYY-MM-DD)
  const convertSelectedDateForAPI = (dateString) => {
    if (!dateString) return null;
    
    // Input format is MM/DD/YYYY from calendar
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Get headers from CSV data
  const extractHeaders = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]);
  };

  // Check if required columns exist
  const checkRequiredColumns = (headers) => {
    const required = ['DUID', 'DATE', 'Comp_1', 'Comp_2', 'Comp_3', 'Comp_4', 'Comp_5', 'Comp_6', 'Total Payout'];
    const missingColumns = required.filter(col => !headers.includes(col));
    
    if (missingColumns.length > 0) {
      setError(`Missing required columns: ${missingColumns.join(', ')}`);
      return false;
    }
    return true;
  };

  // Create file for API (no date conversion)
  const createFileForAPI = (originalFile, data, headers) => {
    const csvRows = [headers.join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => row[header] || '');
      csvRows.push(values.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    
    return new File([blob], originalFile.name, { 
      type: 'text/csv',
      lastModified: new Date().getTime()
    });
  };

  // Process file - NO DATE VALIDATION
  const processFile = () => {
    if (!selectedFile) return;

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Extract headers
        const headers = extractHeaders(results.data);
        setCsvHeaders(headers);
        
        // Check required columns (soft check - only warn)
        const required = ['DUID', 'DATE', 'Comp_1', 'Comp_2', 'Comp_3', 'Comp_4', 'Comp_5', 'Comp_6', 'Total Payout'];
        const missingColumns = required.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          console.warn("Missing recommended columns:", missingColumns);
          // Don't block upload, just warn
        }
        
        // Create file for API - NO DATE CONVERSION
        const newFile = createFileForAPI(selectedFile, results.data, headers);
        setConvertedFile(newFile);
        setError(null);
      },
      error: (error) => {
        setError("CSV file parsing failed: " + error.message);
      }
    });
  };

  useEffect(() => {
    if (selectedFile) {
      processFile();
    }
  }, [selectedFile]);

  const handleDateChange = (date) => {
    setPayoutDate(date);
    setShowCalendar(false);
  };

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const handleFileChange = (file) => {
    if (file) {
      setSelectedFile(file);
      setConvertedFile(null);
      setError(null);
      setCsvHeaders([]);
    } else {
      setSelectedFile(null);
      setConvertedFile(null);
      setCsvHeaders([]);
    }
  };

  const handleDownloadSample = () => {
    const sampleDateStr = "17 Mar 2026";
    const displayDateStr = "17-03-2026";

    const sampleData = [
      ['DUID', 'DATE', 'Comp_1', 'Comp_2', 'Comp_3', 'Comp_4', 'Comp_5', 'Comp_6', 'Total Payout'],
      ['137', sampleDateStr, '1', '2', '2', '2', '2', '1', '10']
    ];
    
    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payout_sample_${displayDateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = debounce(() => {
    if (!isFormValid) {
      setError("Please select a date and upload a file first");
      return;
    }

    if (!convertedFile) {
      setError("File processing failed. Please check your CSV file");
      return;
    }

    const apiFormattedDate = convertSelectedDateForAPI(payoutDate);
    
    if (!apiFormattedDate) {
      setError("Invalid selected date format");
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', convertedFile);
    formData.append('date', apiFormattedDate);

    props.getDataFromAPI(
      "/partner/api/v1/payout-df",
      "POST",
      formData,
      () => {
        setUploading(false);
        setShowSuccessPopup(true);
      },
      (err) => {
        setError(err.message || "File upload failed");
        setUploading(false);
      },
      {
        'Content-Type': 'multipart/form-data',
      }
    );
  }, 300);

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    handleCancel();
    navigate("/home/dfpayout/dfpayoutlist");
  };

  const handleCancel = () => {
    setPayoutDate("");
    setSelectedFile(null);
    setConvertedFile(null);
    setError(null);
    setShowCalendar(false);
    setCsvHeaders([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <PayoutDateComponent
          payoutDate={payoutDate}
          selectedFile={selectedFile}
          onDateChange={handleDateChange}
          onFileChange={handleFileChange}
          onDownloadSample={handleDownloadSample}
          onUpload={handleUpload}
          onCancel={handleCancel}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onCalendarToggle={handleCalendarToggle}
          showCalendar={showCalendar}
          isFormValid={isFormValid}
          uploading={uploading}
          error={error}
          dateMismatch={false}
          showSuccessPopup={showSuccessPopup}
          onSuccessPopupClose={handleSuccessPopupClose}
          csvHeaders={csvHeaders}
        />
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return { state };
}

export default connect(mapStateToProps, { getDataFromAPI })(PayoutDateContainer);